import Button from '@rio-cloud/rio-uikit/Button';
import type { ReactNode } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';

import type { Location } from '#domain/Location.ts';
import type { OrderStatus } from '#domain/Order.ts';
import type { StopType } from '#domain/Stop.ts';
import type { StopStatus } from '#domain/StopStatus.ts';
import { isSameDay } from '#utils/isSameDay.ts';

import { StopAndOrderStatus } from './StopAndOrderStatus.tsx';

type StopInfoProps = {
    location: Location;
    name: ReactNode;
    from: string;
    to: string;
    canEdit?: boolean;
    onEdit?: () => void;
    stopStatus?: StopStatus;
    stopType?: StopType;
    orderStatus?: OrderStatus;
    showStatusInfo?: boolean;
    variant?: 'default' | 'summary';
    orderReference?: string;
};

type StopTimeWindowProps = {
    from: string;
    to: string;
};

export const StopTimeWindow = ({ from, to }: StopTimeWindowProps) => {
    const sameDay = isSameDay(from, to);
    const showToDate = from !== to && !sameDay;
    const showToTime = from !== to;

    return (
        <div className='display-flex align-items-center gap-10 ellipsis-1'>
            <div className='display-flex align-items-center gap-2'>
                <span className='uikit-icon icon-calendar text-color-darker' />
                <span>
                    <FormattedDate value={from} />
                </span>
            </div>
            <div className='display-flex align-items-center gap-2'>
                <span className='uikit-icon icon-time-alt text-color-darker' />
                <span>
                    <FormattedTime value={from} />
                </span>
            </div>
            {showToDate && (
                <>
                    <span className='margin-x-2'>—</span>
                    <div className='display-flex align-items-center gap-2'>
                        <span className='uikit-icon icon-calendar text-color-darker' />
                        <span>
                            <FormattedDate value={to} />
                        </span>
                    </div>
                </>
            )}
            {showToTime && (
                <>
                    {sameDay && <span className='margin-x-2'>—</span>}
                    <div className='display-flex align-items-center gap-2'>
                        <span className='uikit-icon icon-time-alt text-color-darker' />
                        <span>
                            <FormattedTime value={to} />
                        </span>
                    </div>
                </>
            )}
        </div>
    );
};

export const StopInfo = ({
    name,
    location,
    from,
    to,
    canEdit = false,
    onEdit,
    stopStatus,
    stopType,
    orderStatus,
    showStatusInfo = false,
    variant = 'default',
    orderReference,
}: StopInfoProps) => {
    const isSummary = variant === 'summary';
    const isEditable = canEdit && Boolean(onEdit);
    const address = `${location.country ?? ''} - ${location.zipCode ?? ''} ${location.city}, ${location.street}`;
    const hasAddress = [location.country, location.zipCode, location.city, location.street].some(Boolean);
    const primaryText = isSummary && hasAddress ? address : name;

    const heading = (
        <div className={`text-bold ${isSummary ? 'text-size-14' : 'text-size-16'} line-height-18 ellipsis-1`}>
            {primaryText}
        </div>
    );

    return (
        <div className={`display-flex flex-column ${isSummary ? 'gap-5' : 'gap-2 max-width-400'}`}>
            {isEditable && !isSummary ? (
                <Button
                    type='button'
                    bsStyle='unstyled'
                    bsSize={Button.XS}
                    variant='link-inline'
                    className='display-flex align-items-center gap-5 cursor-pointer padding-0 text-left'
                    onClick={onEdit}
                    iconName='pencil'
                    iconRight
                    noRippleEffect
                    style={{
                        alignSelf: 'flex-start',
                        backgroundColor: 'transparent',
                        color: 'inherit',
                        maxWidth: '100%',
                    }}
                >
                    {heading}
                </Button>
            ) : (
                heading
            )}

            {hasAddress && (
                <div className={isSummary ? 'text-size-12 text-color-dark ellipsis-1' : 'text-medium ellipsis-1'}>
                    {isSummary ? name : address}
                </div>
            )}

            <div
                className={`display-flex align-items-center gap-10 ${isSummary ? 'flex-wrap text-size-12 text-color-dark' : 'ellipsis-1'}`}
            >
                <StopTimeWindow from={from} to={to} />
                {orderReference !== undefined && (
                    <span className='display-flex align-items-center gap-5'>
                        <span className='uikit-icon icon-order' aria-hidden='true' />| {orderReference}
                    </span>
                )}
                {isEditable && isSummary && (
                    <Button bsStyle={Button.MUTED} bsSize={Button.XS} iconName='icon-pencil' onClick={onEdit}>
                        <FormattedMessage id='intl-msg:ordercom-cloud-frontend.tours.stop.edit.title' />
                    </Button>
                )}
                {showStatusInfo && (
                    <StopAndOrderStatus stopStatus={stopStatus} stopType={stopType} orderStatus={orderStatus} />
                )}
            </div>
        </div>
    );
};
